import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { subCategoryService } from '../../services/subCategoryService';
import { webCategoryService } from '../../services/webCategoryService';

const SubCategoryForm = ({ subCategory, mode, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      categoryId: subCategory?.categoryId?._id || subCategory?.categoryId || '',
      name: subCategory?.name || '',
      slug: subCategory?.slug || '',
      heading: subCategory?.heading || '',
      description: subCategory?.description || '',
      order: subCategory?.order || 0,
      isActive: subCategory?.isActive !== undefined ? subCategory.isActive : true
    }
  });

  // Fetch categories for dropdown
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await webCategoryService.getParentCategoriesForDropdown({ limit: 100, isActive: true });
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Watch name field to auto-generate slug
  const watchName = watch('name');

  useEffect(() => {
    if (mode === 'create' && watchName && !subCategory) {
      const slug = generateSlug(watchName);
      setValue('slug', slug);
    }
  }, [watchName, mode, setValue, subCategory]);

  // Generate slug from name
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      // Check for duplicate categoryId on create
      if (mode === 'create') {
        const existing = await webCategoryService.getAllCategories({ categoryId: data.categoryId, limit: 1 });
        if (existing.success && existing.data?.length > 0) {
          setError('A subcategory for this category already exists. Please edit the existing one instead.');
          setLoading(false);
          return;
        }
      }

      const payload = {
        ...data,
        image: ''
      };

      let response;
      if (mode === 'create') {
        response = await subCategoryService.createSubCategory(payload);
      } else {
        response = await subCategoryService.updateSubCategory(subCategory._id, payload);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Something went wrong');
      }
    } catch (err) {
      console.error('Error saving subcategory:', err);
      setError(err.response?.data?.message || err.message || 'Error saving subcategory');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Parent Category */}
        <Grid item xs={12}>
          <Controller
            name="categoryId"
            control={control}
            rules={{ required: 'Parent category is required' }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                value={categories.find((cat) => cat.id === value) || null}
                onChange={(_, newValue) => {
                  onChange(newValue?.id || '');
                  if (newValue && mode === 'create') {
                    setValue('slug', newValue.slug || generateSlug(newValue.title || ''));
                    setValue('heading', newValue.title || '');
                    setValue('name', newValue.title || '');
                  }
                }}
                options={categories}
                getOptionLabel={(option) => option.title || ''}
                loading={loadingCategories}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Category"
                    required
                    error={!!errors.categoryId}
                    helperText={errors.categoryId?.message}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCategories ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>

        {/* SubCategory Name */}
        <Grid item xs={12} md={6}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'SubCategory name is required',
              maxLength: {
                value: 100,
                message: 'Name cannot exceed 100 characters'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="SubCategory Name"
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>

        {/* Slug */}
        <Grid item xs={12} md={6}>
          <Controller
            name="slug"
            control={control}
            rules={{
              required: 'Slug is required',
              pattern: {
                value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: 'Slug must be lowercase with hyphens only'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Slug"
                fullWidth
                required
                error={!!errors.slug}
                helperText={errors.slug?.message || 'Auto-generated from name'}
              />
            )}
          />
        </Grid>

        {/* Heading */}
        <Grid item xs={12}>
          <Controller
            name="heading"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Heading (Title)"
                fullWidth
                error={!!errors.heading}
                helperText={errors.heading?.message}
              />
            )}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            rules={{ required: 'Description is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                required
                multiline
                rows={4}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Grid>

        {/* Order */}
        <Grid item xs={12} md={6}>
          <Controller
            name="order"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Display Order"
                type="number"
                fullWidth
                helperText="Lower numbers appear first"
              />
            )}
          />
        </Grid>

        {/* Active Status */}
        <Grid item xs={12} md={6}>
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} color="primary" />}
                label="Active"
              />
            )}
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              mt: 2
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubCategoryForm;
